import React, { useCallback, useMemo, useState } from 'react'
import styled, { useTheme } from 'styled-components'
import { Flex, Text, TextVariant, Button, Modal, Select } from '@mprolab/uikit'
import { useTranslation } from 'react-i18next'
import { ethers } from 'ethers'
import { Contract } from '@ethersproject/contracts'
import { useWeb3React } from 'lib/wagmi-compat/useWeb3React'
import { getSimpleRpcProviderByChainId } from 'utils/providers'
import { useMarketplaceContext } from 'utils/marketplace/useMarketplaceContext'
import { useWeb3ProviderAndContract } from 'hooks/useWeb3ProviderAndContract'
import { ERC721_MIN_ABI } from 'utils/marketplace/abi'
import { readOwnedLicenceTokenIds } from 'utils/marketplace/readOwnedLicences'
import { decodeMarketplaceError } from 'utils/marketplace/errorMessages'
import { useUnclaimedForToken } from 'utils/marketplace/useUnclaimedForToken'
import { reflectLicenceTransfer } from 'utils/marketplace/reflectLicenceTransfer'
import { invalidateLicenceLists } from 'state/services/indexNode/indexNode'
import { useDispatch } from 'react-redux'
import TxConfirmation from 'components/TxConfirmation'
import useToast from 'hooks/useToast'
import { useSwitchChainForContractTx } from 'hooks/useSwitchChainForContractTx'
import { useLicenceNamesByToken } from 'hooks/useLicenceNamesByToken'

const Content = styled(Flex)`
  flex-direction: column;
  gap: 14px;
  margin-top: 20px;
  width: 460px;
  max-height: calc(100vh - 240px);
  overflow-y: auto;

  ${({ theme }) => theme.mediaQueries.mobile} {
    width: 100%;
  }
`

const Field = styled(Flex)`
  flex-direction: column;
  gap: 6px;
`

const Input = styled.input`
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.gray8};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.white};
  font-size: 14px;
  outline: none;
  &:focus {
    border-color: ${({ theme }) => theme.colors.cyan};
  }
`

interface TransferLicenseModalProps {
  /** Collection this licence belongs to. */
  nft: string
  collectionLabel: string
  /**
   * The licence the user opened this from. Opening the modal from a row's menu
   * and then asking which licence they meant throws away the only thing the
   * click already said.
   */
  initialTokenId?: string
  /** Called after a confirmed transfer so the caller can refresh its list. */
  onTransferred?: () => void
  onDismiss?: () => void
}

/**
 * Sends one licence to another wallet.
 *
 * One licence per transfer, on purpose: a bundle is impossible to undo
 * halfway and impossible to explain afterwards. The modal ends by showing the
 * transaction hash — the user's own proof that the licence moved.
 */
const TransferLicenseModal: React.FC<TransferLicenseModalProps> = ({
  nft,
  collectionLabel,
  initialTokenId,
  onTransferred,
  onDismiss
}) => {
  const { t } = useTranslation()
  const { colors } = useTheme()
  const { account, provider } = useWeb3React()
  const ctx = useMarketplaceContext()
  /**
   * Every signing action here must run on Base.
   *
   * Nothing in the marketplace enforced the chain, so an approval signed
   * while the wallet sat on Ethereum went to an address that holds no
   * contract there — MetaMask showed 'no changes' and the listing could
   * never proceed. Every other contract flow in the app already does this.
   */
  const ensureChainForTx = useSwitchChainForContractTx()
  /**
   * A signer bound to Base, not to whatever chain the wallet is sitting on.
   *
   * Switching the wallet is not enough: the provider captured in this
   * render still points at the old chain, so the transaction went out on
   * Ethereum even after a successful switch. Every other contract flow in
   * the app resolves its signer by chain id for exactly this reason.
   */
  const { getProviderOrSignerByChainId } = useWeb3ProviderAndContract()
  const { toastError } = useToast()
  const dispatch = useDispatch()
  // A token id is a unix timestamp; the name is what the holder calls it.
  const licenceNames = useLicenceNamesByToken()

  const [tokenIds, setTokenIds] = useState<string[] | null>(null)
  const [tokenId, setTokenId] = useState(initialTokenId ?? '')
  const [recipient, setRecipient] = useState('')
  const [busy, setBusy] = useState(false)
  const [txHash, setTxHash] = useState<string | null>(null)

  React.useEffect(() => {
    let cancelled = false
    const run = async () => {
      if (!account) return
      const rpc = getSimpleRpcProviderByChainId(
        ctx.chainId
      ) as ethers.providers.Provider
      try {
        const ids = await readOwnedLicenceTokenIds({ nft, owner: account, provider: rpc })
        if (!cancelled) {
          setTokenIds(ids)
          // Keep the row's licence if it is still owned; otherwise fall back
          // to the only one there is.
          setTokenId((current) => {
            if (current && ids.includes(current)) return current
            return ids.length === 1 ? ids[0] : ''
          })
        }
      } catch {
        if (!cancelled) setTokenIds([])
      }
    }
    void run()
    return () => {
      cancelled = true
    }
  }, [account, nft, ctx.chainId])

  // The unclaimed distribution goes with the licence. A sender who does not
  // know that gives it away by accident, which is the whole reason this is
  // shown next to the button rather than in the documentation.
  const unclaimed = useUnclaimedForToken(nft, tokenId || undefined)

  const recipientValid = useMemo(
    () => ethers.utils.isAddress(recipient.trim()),
    [recipient]
  )
  const sameWallet = useMemo(
    () =>
      !!account && recipient.trim().toLowerCase() === account.toLowerCase(),
    [recipient, account]
  )

  const onTransfer = useCallback(async () => {
    if (!provider || !account || !tokenId || !recipientValid) return
    if (!(await ensureChainForTx(ctx.chainId))) return
    setBusy(true)
    try {
      const signer = getProviderOrSignerByChainId(ctx.chainId, account)
      const c = new Contract(
        nft,
        ERC721_MIN_ABI as unknown as ethers.ContractInterface,
        signer
      )
      const tx = await c.transferFrom(account, recipient.trim(), tokenId)
      setTxHash(tx.hash)
      await tx.wait()
      // The chain has moved the licence; make the platform's registry say so
      // BEFORE the table refetches, or the refetch re-reads the stale row and
      // the licence lingers in this list until someone forces a sync.
      await reflectLicenceTransfer(nft, tokenId)
      // Not every place that can open this modal owns a refetch handle, and a
      // cached list outlives the modal that closed over it.
      dispatch(invalidateLicenceLists())
      // The licence has left this wallet: drop it from the picker straight
      // away and let the caller refresh the table, rather than leaving both
      // showing something the chain no longer agrees with.
      setTokenIds((ids) => (ids ?? []).filter((id) => id !== tokenId))
      setTokenId('')
      onTransferred?.()
    } catch (e) {
      toastError(decodeMarketplaceError(e, t))
      setTxHash(null)
    } finally {
      setBusy(false)
    }
  }, [
    provider,
    account,
    tokenId,
    recipient,
    recipientValid,
    nft,
    ctx.chainId,
    ensureChainForTx,
    getProviderOrSignerByChainId,
    dispatch,
    onTransferred,
    toastError,
    t
  ])

  return (
    <Modal title={t('licenses.transferTitle')} onDismiss={onDismiss}>
      <Content>
        {txHash ? (
          <TxConfirmation
            hash={txHash}
            chainId={ctx.chainId}
            title={t('licenses.transferSent')}
          />
        ) : (
          <>
            <Text variant={TextVariant.BODY_SMALL} color={colors.gray6}>
              {t('licenses.transferHint')}
            </Text>

            <Field>
              <Text variant={TextVariant.BODY_SMALL_BOLD}>
                {t('licenses.transferWhich')}
              </Text>
              {tokenIds === null ? (
                <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
                  {t('marketplace.loadingLicences')}
                </Text>
              ) : tokenIds.length === 0 ? (
                <Text color={colors.red} variant={TextVariant.BODY_SMALL}>
                  {t('marketplace.noLicencesHere')}
                </Text>
              ) : (
                <Select
                  value={tokenId}
                  onChange={(e) => setTokenId(String(e.target.value))}
                  placeholder={t('marketplace.chooseLicencePlaceholder')}
                  items={tokenIds.map((id) => ({
                    value: id,
                    label: licenceNames[id]
                      ? `${licenceNames[id]} · ${collectionLabel} #${id}`
                      : `${collectionLabel} #${id}`
                  }))}
                  fullWidth
                />
              )}
            </Field>

            <Field>
              <Text variant={TextVariant.BODY_SMALL_BOLD}>
                {t('licenses.transferTo')}
              </Text>
              <Input
                placeholder="0x…"
                value={recipient}
                onChange={(e) => setRecipient(e.target.value)}
                spellCheck={false}
              />
              {recipient.trim() && !recipientValid ? (
                <Text color={colors.red} variant={TextVariant.TEXT_XS_REGULAR}>
                  {t('licenses.transferBadAddress')}
                </Text>
              ) : null}
              {sameWallet ? (
                <Text color={colors.red} variant={TextVariant.TEXT_XS_REGULAR}>
                  {t('licenses.transferSameWallet')}
                </Text>
              ) : null}
              <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
                {t('licenses.transferIrreversible')}
              </Text>
              {unclaimed.unclaimedMpro !== null && unclaimed.unclaimedMpro > 0 && (
                <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.yellow}>
                  {t('marketplace.transferCarriesUnclaimed', {
                    amount: unclaimed.unclaimedMpro.toLocaleString('en-US', {
                      maximumFractionDigits: 2
                    })
                  })}
                </Text>
              )}
            </Field>

            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={busy || !tokenId || !recipientValid || sameWallet}
              onClick={() => void onTransfer()}
            >
              {busy ? t('licenses.transferring') : t('licenses.transferAction')}
            </Button>
          </>
        )}
      </Content>
    </Modal>
  )
}

export default TransferLicenseModal
